import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { fieldChecked, fieldValue } from "../../app/components/polaris";
import type { StorefrontThemeSettings } from "../../app/models/StorefrontThemeSettings";
import { SETTINGS_SECTIONS } from "../../app/terminology/settings";
import useStorefrontSettings from "../storefront/useStorefrontSettings";
import SettingsPageLayout from "./SettingsPageLayout";

type ThemeKey = keyof StorefrontThemeSettings;

const legacyOptions: { key: ThemeKey; label: string }[] = [
    { key: "collectionRedirectEnabled", label: "Use collection filtering as a fallback" },
    { key: "ymmEnabled", label: "Use Year, Make, Model order" },
    { key: "sortYearsASC", label: "Sort years in ascending order" },
    { key: "isPartialSearch", label: "Allow partial search" },
    { key: "useTwoTags", label: "Add Make-Model tags" },
    { key: "useThreeTags", label: "Add Make and Make-Model tags" },
    { key: "includeUniversalCollections", label: "Include universal collections" },
];

const moreLinks = [
    { label: SETTINGS_SECTIONS.plan, to: "/plan" },
    { label: "Setup & installation", to: "/setup-health" },
];

function AdvancedSettingsPage() {
    const navigate = useNavigate();
    const storefront = useStorefrontSettings();
    const { themeDraft: theme, resultsDraft, saving, updateThemeDraft } = storefront;

    if (storefront.loading || !theme) return storefront.error
        ? <SettingsPageLayout error={storefront.error}>{null}</SettingsPageLayout>
        : <SettingsPageLayout loading>{null}</SettingsPageLayout>;

    const themeSwitch = (key: ThemeKey, label: string, checked = !!theme[key]) => (
        <s-switch key={key} label={label} checked={checked} disabled={saving}
            onChange={event => updateThemeDraft(key, fieldChecked(event))} />
    );

    return (
        <SettingsPageLayout error={storefront.error} form={storefront.form}>
            <s-section heading="Search & navigation">
                {themeSwitch("onlyShowMappedUserVehicles", "Only show mapped user vehicles", theme.onlyShowMappedUserVehicles ?? true)}
            </s-section>
            <s-section heading="Compatibility">
                <s-stack gap="base">
                    {resultsDraft?.renderingMode === "CollectionFiltering"
                        && themeSwitch("isCollectionMetaField", "Preselect make from a collection metafield")}
                    {themeSwitch("saveTagToCustomerProfile", "Save vehicle tags to customer profiles")}
                </s-stack>
            </s-section>
            {theme.collectionTagFilterEnabled && <s-section heading="Custom filter URL">
                <s-text-field label="Querystring filter pattern" value={theme.collectionTagFilterPattern || ""}
                    placeholder="?filter.p.tag={vehicle}" disabled={saving} autocomplete="off"
                    onInput={event => updateThemeDraft("collectionTagFilterPattern", fieldValue(event))} />
            </s-section>}
            <s-section heading="Legacy settings">
                <s-stack gap="base">
                    <s-switch label="Enable submodel filter" checked={storefront.submodelEnabled} disabled={saving}
                        onChange={event => storefront.setSubmodelEnabled(fieldChecked(event))} />
                    {legacyOptions.map(option => themeSwitch(option.key, option.label))}
                </s-stack>
            </s-section>
            <s-section heading="Plan and setup" padding="none">
                <s-box padding="small-400">
                    {moreLinks.map((link, index) => <Fragment key={link.to}>
                        {index > 0 && <s-box paddingInline="small-100"><s-divider /></s-box>}
                        <s-clickable padding="small-100" borderRadius="base" href={link.to}
                            onClick={event => { event.preventDefault(); navigate(link.to); }}>
                            <s-grid gridTemplateColumns="1fr auto" alignItems="center" gap="base">
                                <s-text>{link.label}</s-text>
                                <s-icon type="chevron-right" />
                            </s-grid>
                        </s-clickable>
                    </Fragment>)}
                </s-box>
            </s-section>
        </SettingsPageLayout>
    );
}

export default observer(AdvancedSettingsPage);
